import { SEO } from "@/components/seo/SEO";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import { Button } from "@/components/ui/button";

const InstitutionTermsOfService = () => {
  return (
    <>
      <SEO 
        title="Institution Partnership Terms of Service | Careerfy"
        description="Read the Terms of Service for Careerfy's Institution Partnership program for colleges and universities."
        path="/institution/terms-of-service"
        structuredData={{
          "@context": "https://schema.org",
          "@type": "WebPage",
          name: "Institution Partnership Terms of Service",
          url: "/institution/terms-of-service"
        }}
      />
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-4xl mx-auto px-4 py-12">
          <Button
            variant="ghost"
            asChild
            className="mb-6 text-muted-foreground hover:text-foreground"
          >
            <Link to="/">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
          </Button>

          <h1 className="text-3xl font-bold text-primary mb-8">Institution Partnership Terms of Service</h1>
          
          <div className="space-y-8 text-gray-600">
            <section>
              <h2 className="text-xl font-semibold text-primary mb-4">1. Agreement to Terms</h2>
              <p>By accessing and using Careerfy's Institution Partnership services provided by HeyLiving Club LLP, educational institutions and their representatives agree to be bound by these Terms of Service. If you disagree with any part of these terms, you may not access our services.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-4">2. Description of Service</h2>
              <p className="mb-3">The Institution Partnership program provides colleges and universities with:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Dedicated institution landing pages for student registration</li>
                <li>Training & Placement Officer (TPO) dashboard for monitoring student progress</li>
                <li>Aggregated analytics and reporting on student interview readiness</li>
                <li>AI-powered interview preparation services for registered students</li>
                <li>Resume analysis and career guidance tools</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-4">3. Institution Responsibilities</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Institutions must provide accurate information during registration</li>
                <li>TPO accounts must be managed by authorized institutional representatives</li>
                <li>Institutions are responsible for communicating the partnership to their students</li>
                <li>Sharing of student data analytics must comply with institutional policies</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-4">4. Student Accounts</h2>
              <p className="mb-3">Students registering through institution links:</p>
              <ul className="list-disc pl-6 space-y-2">
                <li>Receive benefits specific to the institution partnership</li>
                <li>Their aggregated, anonymized data may be shared with institutional TPOs</li>
                <li>Must provide accurate information during registration</li>
                <li>Are responsible for maintaining account security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-4">5. Data Usage and Privacy</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Individual student data is protected and not shared without consent</li>
                <li>TPOs can access aggregated analytics for their institution's students only</li>
                <li>Interview recordings and personal responses remain private to individual students</li>
                <li>Analytics include readiness scores, activity metrics, and progress tracking</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-4">6. Service Limitations</h2>
              <ul className="list-disc pl-6 space-y-2">
                <li>Services are provided "as is" without guarantees of employment outcomes</li>
                <li>Careerfy reserves the right to modify or discontinue features</li>
                <li>Service availability may vary based on partnership agreements</li>
              </ul>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-4">7. Intellectual Property</h2>
              <p>All content, features, and functionality of the Careerfy platform are owned by HeyLiving Club LLP and are protected by intellectual property laws. Institutions may not reproduce, distribute, or create derivative works without explicit permission.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-4">8. Termination</h2>
              <p>Either party may terminate the partnership with reasonable notice. Upon termination, student accounts remain active but are disassociated from the institution. Historical analytics data may be retained for compliance purposes.</p>
            </section>

            <section>
              <h2 className="text-xl font-semibold text-primary mb-4">9. Contact Information</h2>
              <p className="mb-3">For any queries regarding these terms or the Institution Partnership program, please contact us:</p>
              <ul className="list-none space-y-2">
                <li><strong>Company:</strong> HeyLiving Club LLP</li>
                <li><strong>Email:</strong> Info@careerfy.ai</li>
                <li><strong>Phone:</strong> +91 9552526258</li>
              </ul>
            </section>

            <section className="pt-4 border-t">
              <p className="text-sm text-muted-foreground">
                Last updated: December 2024
              </p>
            </section>
          </div>
        </div>
      </div>
    </>
  );
};

export default InstitutionTermsOfService;
